export type AlertScaleThresholds = [
  LowerErrorThreshold,
  LowerWarningThreshold,
  HigherWarningThreshold,
  HigherErrorThreshold
];
export type LowerErrorThreshold = number;
export type LowerWarningThreshold = number;
export type HigherWarningThreshold = number;
export type HigherErrorThreshold = number;
export type LowerWarningVector = [LowerErrorThreshold, LowerWarningThreshold];
export type HigherWarningVector = [
  HigherWarningThreshold,
  HigherErrorThreshold
];
export class AlertScale {
  private lowerErrorLevel: LowerErrorThreshold;
  private lowerWarningLevel: LowerWarningThreshold;
  private higherWarningLevel: HigherWarningThreshold;
  private higherErrorLevel: HigherErrorThreshold;

  private constructor(threatLevelBoundaries: AlertScaleThresholds) {
    const sortedBoundaries = threatLevelBoundaries.sort((a, b) => a - b);
    this.lowerErrorLevel = sortedBoundaries[0];
    this.lowerWarningLevel = sortedBoundaries[1];
    this.higherWarningLevel = sortedBoundaries[2];
    this.higherErrorLevel = sortedBoundaries[3];
  }

  static from(
    lowerWarningLevel: LowerErrorThreshold,
    lowerErrorLevel: LowerWarningThreshold,
    higherWarningLevel: HigherWarningThreshold,
    higherErrorLevel: HigherErrorThreshold
  ) {
    return new AlertScale([
      lowerWarningLevel,
      lowerErrorLevel,
      higherWarningLevel,
      higherErrorLevel
    ]);
  }

  static fromWarningVectors(
    underAcceptableValuesLevels: LowerWarningVector,
    upperAcceptableValuesLevels: HigherWarningVector
  ) {
    return new AlertScale([
      underAcceptableValuesLevels[0],
      underAcceptableValuesLevels[1],
      upperAcceptableValuesLevels[0],
      upperAcceptableValuesLevels[1]
    ]);
  }

  static fromThresholds(boundaries: AlertScaleThresholds) {
    return new AlertScale(boundaries);
  }

  static onlyErrors(
    lowerErrorThreshold: LowerErrorThreshold,
    higherErrorThreshold: HigherErrorThreshold
  ) {
    return new AlertScale([
      lowerErrorThreshold,
      lowerErrorThreshold,
      higherErrorThreshold,
      higherErrorThreshold
    ]);
  }

  static onlyHighError(higherErrorThreshold: HigherErrorThreshold) {
    return new AlertScale([
      -Infinity,
      -Infinity,
      higherErrorThreshold,
      higherErrorThreshold
    ]);
  }

  match<T>(
    inspectedValue: number,
    callbacks: {
      Ok: (candidateValue: number) => T;
      Warning: (candidateValue: number) => T;
      Error: (candidateValue: number) => T;
    }
  ): T {
    if (inspectedValue < this.lowerErrorLevel) {
      return callbacks.Error(inspectedValue);
    } else if (
      inspectedValue >= this.lowerErrorLevel &&
      inspectedValue < this.lowerWarningLevel
    ) {
      return callbacks.Warning(inspectedValue);
    } else if (
      inspectedValue >= this.lowerWarningLevel &&
      inspectedValue <= this.higherWarningLevel
    ) {
      return callbacks.Ok(inspectedValue);
    } else if (
      inspectedValue > this.higherWarningLevel &&
      inspectedValue <= this.higherErrorLevel
    ) {
      return callbacks.Warning(inspectedValue);
    } else {
      return callbacks.Error(inspectedValue);
    }
  }
}
